import { useRef, useEffect, useState } from 'react'

const ParticlePortrait = () => {
  const canvasRef = useRef(null)
  const mouseRef = useRef({ x: -1000, y: -1000, active: false })
  const linesRef = useRef([])
  const imageLoadedRef = useRef(false)
  const startTimeRef = useRef(null)
  const [size, setSize] = useState(400)

  // Responsive canvas size
  useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth
      if (width <= 480) {
        setSize(Math.min(200, width - 40))
      } else if (width <= 768) {
        setSize(Math.min(260, width - 60))
      } else {
        setSize(380)
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  // Canvas draw loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    canvas.width = size
    canvas.height = size

    let animationId

    const img = new Image()
    img.src = '/profile-akshata.png'

    img.onload = () => {
      // Draw image into offscreen canvas to sample pixels
      const offscreen = document.createElement('canvas')
      const offCtx = offscreen.getContext('2d')
      offscreen.width = size
      offscreen.height = size

      const scale = 0.85
      const imgAspect = img.width / img.height
      let drawWidth = size * scale
      let drawHeight = drawWidth / imgAspect
      if (drawHeight > size * scale) {
        drawHeight = size * scale
        drawWidth = drawHeight * imgAspect
      }
      const offsetX = (size - drawWidth) / 2
      const offsetY = (size - drawHeight) / 2
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)

      const { data: pixels } = offCtx.getImageData(0, 0, size, size)
      const lines = []
      const rowGap = size <= 260 ? 5 : 6

      for (let y = 0; y < size; y += rowGap) {
        let x = 0
        while (x < size) {
          const i = (y * size + x) * 4
          const alpha = pixels[i + 3]

          if (alpha > 128) {
            const r = pixels[i]
            const g = pixels[i + 1]
            const b = pixels[i + 2]
            const brightness = (r + g + b) / (3 * 255)
            const lineLength = Math.floor(3 + brightness * (size <= 260 ? 8 : 14))

            lines.push({
              x: x + (Math.random() - 0.5) * 300,
              y: y + (Math.random() - 0.5) * 300,
              targetX: x,
              targetY: y,
              vx: 0,
              vy: 0,
              length: lineLength,
              baseAlpha: 0.5 + brightness * 0.5,
              currentAlpha: 0,
              delay: Math.random() * 0.3,
            })
            x += lineLength + 3
          } else {
            x += 4
          }
        }
      }

      linesRef.current = lines
      imageLoadedRef.current = true
      startTimeRef.current = performance.now()
    }

    const draw = () => {
      animationId = requestAnimationFrame(draw)
      ctx.clearRect(0, 0, size, size)
      if (!imageLoadedRef.current) return

      const lines = linesRef.current
      const mouse = mouseRef.current
      const elapsed = (performance.now() - startTimeRef.current) / 1000

      lines.forEach((p) => {
        const t = elapsed - p.delay
        if (t < 0) return

        // Fade in
        const fadeProgress = Math.min(t / 1.5, 1)
        p.currentAlpha = p.baseAlpha * (1 - Math.pow(1 - fadeProgress, 2))

        // Move toward target
        const moveProgress = Math.min(t / 2.5, 1)
        const easedMove = 1 - Math.pow(1 - moveProgress, 3)

        // Mouse repulsionnb
        if (mouse.active) {
          const dx = p.x - mouse.x
          const dy = p.y - mouse.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < 70 && dist > 0) {
            const force = (1 - dist / 70) * 2.2
            p.vx += (dx / dist) * force
            p.vy += (dy / dist) * force
          }
        }

        // Spring pull to target
        const pull = 0.01 + easedMove * 0.07
        p.vx += (p.targetX - p.x) * pull
        p.vy += (p.targetY - p.y) * pull
        p.vx *= 0.92
        p.vy *= 0.92
        p.x += p.vx
        p.y += p.vy

        // Draw line stroke — lavender-purple tinted to match the theme
        ctx.strokeStyle = `rgba(195, 177, 225, ${p.currentAlpha})`
        ctx.lineWidth = size <= 260 ? 1.5 : 2
        ctx.beginPath()
        ctx.moveTo(p.x, p.y)
        ctx.lineTo(p.x + p.length, p.y)
        ctx.stroke()
      })
    }

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top, active: true }
    }
    const onTouchMove = (e) => {
      const rect = canvas.getBoundingClientRect()
      const t = e.touches[0]
      mouseRef.current = { x: t.clientX - rect.left, y: t.clientY - rect.top, active: true }
    }
    const onLeave = () => { mouseRef.current.active = false }

    canvas.addEventListener('mousemove', onMouseMove)
    canvas.addEventListener('mouseleave', onLeave)
    canvas.addEventListener('touchmove', onTouchMove)
    canvas.addEventListener('touchend', onLeave)

    draw()

    return () => {
      cancelAnimationFrame(animationId)
      canvas.removeEventListener('mousemove', onMouseMove)
      canvas.removeEventListener('mouseleave', onLeave)
      canvas.removeEventListener('touchmove', onTouchMove)
      canvas.removeEventListener('touchend', onLeave)
    }
  }, [size])

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, cursor: 'crosshair', display: 'block' }}
    />
  )
}

export default ParticlePortrait
